import { useEffect, useRef, useState } from 'react';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const stats = [
  {
    icon: 'ri-calendar-check-line',
    value: 10,
    suffix: '+',
    label: 'Años de experiencia',
  },
  {
    icon: 'ri-building-line',
    value: 5,
    suffix: '+',
    label: 'Cooperativas activas',
  },
  {
    icon: 'ri-user-line',
    value: 2000,
    suffix: '+',
    label: 'Afiliados gestionados',
  },
  {
    icon: 'ri-file-list-3-line',
    value: 50000,
    suffix: '+',
    label: 'Recibos generados',
  },
];

function useCountUp(end: number, duration: number, start: boolean) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    let animationId: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));

      if (progress < 1) {
        animationId = requestAnimationFrame(animate);
      }
    };

    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, [end, duration, start]);

  return count;
}

function StatCard({ stat, isVisible, delay }: { stat: typeof stats[0]; isVisible: boolean; delay: number }) {
  const count = useCountUp(stat.value, 2000, isVisible);

  const formatNumber = (n: number) => {
    if (n >= 1000) {
      return n.toLocaleString('es-BO');
    }
    return n.toString();
  };

  return (
    <div
      className={`text-center transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-white/10 flex items-center justify-center mx-auto mb-4">
        <i className={`${stat.icon} text-2xl md:text-3xl text-accent-300`} />
      </div>
      <div className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white">
        {formatNumber(count)}
        <span className="text-accent-400">{stat.suffix}</span>
      </div>
      <p className="mt-2 text-sm md:text-base text-white/70 font-medium">
        {stat.label}
      </p>
    </div>
  );
}

export default function Stats() {
  const { ref: sectionRef, isVisible } = useScrollReveal<HTMLDivElement>({ threshold: 0.3 });

  return (
    <section id="estadisticas" className="relative py-20 md:py-28 bg-primary-900 overflow-hidden">
      {/* Background texture */}
      <div className="absolute inset-0 texture-dots opacity-10" />
      <div className="absolute inset-0 bg-gradient-to-b from-primary-950/50 to-primary-900/50" />

      <div className="relative w-full px-4 md:px-8 lg:px-12">
        <div ref={sectionRef} className="max-w-5xl mx-auto">
          {/* Header */}
          <div className={`text-center mb-14 md:mb-18 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <span className="inline-block px-4 py-1.5 rounded-full bg-accent-500/20 text-accent-300 text-sm font-semibold mb-4">
              Resultados que respaldan
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white">
              Acualys-Bo en números
            </h2>
            <p className="mt-4 text-base md:text-lg text-white/60 max-w-xl mx-auto">
              Más de una década ayudando a cooperativas de agua a operar de forma eficiente y transparente.
            </p>
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
            {stats.map((stat, i) => (
              <StatCard
                key={stat.label}
                stat={stat}
                isVisible={isVisible}
                delay={i * 200}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}